import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";

const navItems = [
  { to: "/", label: "首页" },
  { to: "/categories", label: "漫画分类" },
  { to: "/about", label: "关于我们" },
];

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2 whitespace-nowrap" aria-label="漫悦漫画网 首页">
      <span className="relative flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
        <i className="ri-book-open-line text-lg" aria-hidden="true" />
      </span>
      <span className="font-heading text-xl leading-none text-foreground-950">
        漫悦<span className="text-primary-600">漫画</span>网
      </span>
    </Link>
  );
}

function ThemeToggle() {
  const [dark, setDark] = useState(false);
  const toggle = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
  };
  return (
    <button
      type="button"
      onClick={toggle}
      className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-background-200 text-foreground-700 transition-colors hover:bg-background-300"
      aria-label={dark ? "切换为白天模式" : "切换为夜间模式"}
    >
      <i className={`${dark ? "ri-sun-line" : "ri-moon-line"} text-lg`} aria-hidden="true" />
    </button>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (keyword.trim()) {
      navigate(`/categories?q=${encodeURIComponent(keyword.trim())}`);
      setOpen(false);
    }
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b transition-all duration-300 ${
        scrolled
          ? "border-background-200 bg-background-50/90 backdrop-blur"
          : "border-transparent bg-transparent"
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 md:px-6" aria-label="主导航">
        <Logo />

        <div className="hidden items-center gap-1 md:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary-100 text-primary-700"
                    : "text-foreground-700 hover:bg-background-200 hover:text-foreground-950"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          <form onSubmit={submitSearch} className="flex items-center overflow-hidden rounded-full bg-background-200 focus-within:ring-2 focus-within:ring-primary-400">
            <i className="ri-search-line ml-3 text-sm text-foreground-500" aria-hidden="true" />
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索漫画…"
              className="w-44 bg-transparent px-2 py-2 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none"
              aria-label="搜索漫画"
            />
            <button type="submit" className="cursor-pointer rounded-full bg-primary-500 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-primary-600">
              搜索
            </button>
          </form>
          <ThemeToggle />
        </div>

        <div className="flex items-center gap-2 md:hidden">
          <ThemeToggle />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg bg-background-200 text-foreground-700"
            aria-label="打开菜单"
          >
            <i className={`${open ? "ri-close-line" : "ri-menu-line"} text-lg`} aria-hidden="true" />
          </button>
        </div>
      </nav>

      {open && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-4 pt-2 md:hidden">
          <form onSubmit={submitSearch} className="mb-3 flex items-center overflow-hidden rounded-full bg-background-200">
            <i className="ri-search-line ml-3 text-sm text-foreground-500" aria-hidden="true" />
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索漫画…"
              className="w-full bg-transparent px-2 py-2 text-sm text-foreground-950 focus:outline-none"
              aria-label="搜索漫画"
            />
            <button type="submit" className="cursor-pointer rounded-full bg-primary-500 px-4 py-2 text-sm text-background-50">
              搜索
            </button>
          </form>
          <div className="flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `rounded-md px-4 py-2 text-sm font-medium ${
                    isActive ? "bg-primary-100 text-primary-700" : "text-foreground-700"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}